import { c as _c } from "react/compiler-runtime";
import { JobStatusResponse } from '@/api/generated/model';
import { CircularProgress } from '@/comps/primitives/circular-progress';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getBulkJobStatus } from '@/api/generated/async-jobs/async-jobs';
function isTerminal(status: JobStatusResponse['status']) {
  return status === 'completed' || status === 'failed';
}
function getJobContribution(job: JobStatusResponse) {
  return isTerminal(job.status) ? 100 : job.progress;
}
function aggregateProgress(jobs: JobStatusResponse[], jobIds: string[]) {
  const jobMap = new Map(jobs.map(job => [job.id, job]));
  const n = jobIds.length;
  if (n === 0) return 0;
  const total = jobIds.reduce((sum, id) => {
    const job = jobMap.get(id);
    if (!job) return sum;
    return sum + getJobContribution(job);
  }, 0);
  return total / n;
}
function allJobsTerminal(jobs: JobStatusResponse[], jobIds: string[]) {
  const jobMap = new Map(jobs.map(job => [job.id, job]));
  return jobIds.every(id => {
    const job = jobMap.get(id);
    return job && isTerminal(job.status);
  });
}
export function ToastContent(t0) {
  const $ = _c(34);
  const {
    title,
    jobIds,
    toastIdRef,
    onSuccess,
    onPoll,
    onStartProgress
  } = t0;
  const queryClient = useQueryClient();
  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(title);
  const startedProgressRef = useRef(false);
  const completionHandledRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== jobIds) {
    t1 = t3 => {
      const {
        signal
      } = t3;
      return getBulkJobStatus({
        job_ids: jobIds
      }, {
        signal
      });
    };
    t2 = ["getBulkJobStatus", jobIds];
    $[0] = jobIds;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = jobIds.length > 0;
  let t4;
  if ($[3] !== jobIds) {
    t4 = data => {
      if (allJobsTerminal(data?.jobs ?? [], jobIds)) {
        return false;
      }
      return 1000;
    };
    $[3] = jobIds;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== jobIds || $[6] !== onPoll || $[7] !== onStartProgress || $[8] !== onSuccess || $[9] !== queryClient || $[10] !== title || $[11] !== toastIdRef) {
    t5 = async data_0 => {
      onPoll?.();
      const jobs = data_0?.jobs ?? [];
      const progress = aggregateProgress(jobs, jobIds);
      if (progress > 0 && !startedProgressRef.current) {
        onStartProgress?.();
        startedProgressRef.current = true;
      }
      if (!allJobsTerminal(jobs, jobIds) || completionHandledRef.current) {
        return;
      }
      completionHandledRef.current = true;
      const jobMap = new Map(jobs.map(_temp));
      const trackedJobs = jobIds.map(id => jobMap.get(id)).filter(Boolean) as JobStatusResponse[];
      const completedCount = trackedJobs.filter(_temp2).length;
      const failedCount = trackedJobs.filter(_temp3).length;
      const n = jobIds.length;
      if (failedCount === 0) {
        const toastId = toastIdRef?.();
        if (toastId) {
          setTimeout(() => toast.dismiss(toastId), 5000);
        }
        await onSuccess?.();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
        });
        return;
      }
      if (completedCount === 0) {
        setError(true);
        return;
      }
      setWarning(true);
      setDisplayTitle(`${title} — ${completedCount} of ${n} done, ${failedCount} failed`);
      await onSuccess?.();
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
      });
    };
    $[5] = jobIds;
    $[6] = onPoll;
    $[7] = onStartProgress;
    $[8] = onSuccess;
    $[9] = queryClient;
    $[10] = title;
    $[11] = toastIdRef;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setError(true);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t7 = {
      queryFn: t1,
      queryKey: t2,
      enabled: t3,
      cacheTime: 0,
      keepPreviousData: true,
      refetchInterval: t4,
      onSuccess: t5,
      onError: t6
    };
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    data: bulkJobStatus
  } = useQuery(t7);
  bulkJobStatus?.jobs;
  let t8;
  if ($[20] !== bulkJobStatus?.jobs) {
    t8 = bulkJobStatus?.jobs ?? [];
    $[20] = bulkJobStatus?.jobs;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== jobIds || $[23] !== t8) {
    t9 = aggregateProgress(t8, jobIds);
    $[22] = jobIds;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const progress_0 = t9;
  let t10;
  if ($[25] !== displayTitle) {
    t10 = <span className="text-sm font-normal">{displayTitle}</span>;
    $[25] = displayTitle;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== error || $[28] !== progress_0 || $[29] !== warning) {
    t11 = <CircularProgress error={error} warning={warning} value={progress_0} size={20} circleStrokeWidth={2} progressStrokeWidth={2} />;
    $[27] = error;
    $[28] = progress_0;
    $[29] = warning;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = <div className="flex items-center justify-between gap-2">{t10}{t11}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp3(job_1) {
  return job_1.status === "failed";
}
function _temp2(job_0) {
  return job_0.status === "completed";
}
function _temp(job) {
  return [job.id, job];
}
export function useJobToast() {
  const spawnToast = (jobIds: string | string[], title: string, onSuccess?: () => void | Promise<void>, onPoll?: () => void, onStartProgress?: () => void) => {
    const ids = Array.isArray(jobIds) ? jobIds : [jobIds];
    if (ids.length === 0) {
      void onSuccess?.();
      return;
    }
    let toastId: string | number;
    toastId = toast(<ToastContent title={title} jobIds={ids} toastIdRef={() => toastId} onSuccess={onSuccess} onPoll={onPoll} onStartProgress={onStartProgress} />, {
      duration: Infinity,
      dismissible: true,
      classNames: {
        content: 'w-full!'
      }
    });
    return toastId;
  };
  return {
    spawnToast
  };
}